/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

/* eslint-disable max-len -- verbatim prototype strings and golden rows exceed the line limit */
// pcg-core: Medaica Report evidence-package builder (core-viewer.js 352-831).
// This is the pure part of the prototype's export path; the POST /handoff/pcg/latest
// + localStorage plumbing (exportToCardiacReport, 832-875) is NOT vendored — the
// portal posts the package to the pcg-review export API instead.
//
// Deviations (see MODULE-MAP.md):
// - calculationVersion is hard-set to 'canonical-v0.7.1' (allow-list deleted).
// - window.__medaicaExamQuestionnaire / __medaicaExamPatientContext /
//   __medaicaExamRecordingNotes become options.examContext fields.
// - Wall-clock and identity inputs (createdAt, recordingId, examId) are injectable
//   via options for deterministic tests; defaults preserve prototype behavior.
// - options.reviewStatus replaces window.__medaicaCurrentReviewStatus (handoff
//   v0.8.3): the recording's review state, "accepted" | "rejected" | "noted" |
//   "unreviewed" | null. "accepted" stamps evidenceFeeds.pcgViewer.status and
//   clinicianAttestation.state as "attested"; anything else is passed through as
//   the feed status (defaulting to "unreviewed").
// - options.timingReconciliation replaces window.__medaicaTimingReviewDisposition
//   (handoff v0.8.3): the exam-level timing review disposition recorded by the
//   reviewing clinician, exported verbatim as
//   timingCalculationAudit.examTimingReconciliation (null when none).
// - options.hcpConfirmedBpm replaces the viewer's lookup of the selected
//   recording's note (Medaica Viewer v0.9.1 `hcpConfirmedBpmNote`): the
//   heart rate the reviewer confirmed on THIS recording, if any. The caller
//   decides whether it applies (upstream: only when the recording is accepted
//   for Timing). When given, the package's heartRate block reports it as the
//   accepted value and keeps the automated rate alongside; the exam-level
//   roll-up (pcg-report exam-rollup.js) applies the same substitution per
//   recording from the review snapshot, so the option is not required.
// - Comparison labels name what the automated value is: for a guide whose
//   timing engine selected an Evaluation Range (ctx.evaluationRange), upstream's
//   labels (`viewer_eval_range`, `viewer_eval_range_bpm` / `viewerEvalRangeBpm`,
//   "automated Eval Range estimate"); for every other guide, the rate over the
//   viewer's selected usable cycles (`viewer_curated_cycles`,
//   `viewer_curated_cycle_bpm` / `viewerCuratedCycleBpm`, "automated estimate"),
//   since there is no range behind it.
// - One canonical timing result for a timing-engine guide: the package's
//   heart rate, SYS:DIA and timing variability are those of the Evaluation
//   Range (timingStats / timingConsistency over the range's markers), and the
//   top-level `evaluationRange` block records where they came from
//   (evaluationRangeProvenance: the range, the engine package, the calculation
//   methods and the engine's S1-to-S1 interval CV, kept apart from the reported
//   timing variability); absent for a guide without the engine. When the engine
//   is present but not ready (the viewer's "Analysis unavailable" state, where
//   every Provisional Results value reads "Unavailable") the package carries no
//   timing or Energy values: heart rate, SYS:DIA, the durations and the timing
//   variability read "Not assessed" / null, as for any recording without usable
//   timing, and the Structural Sound blocks read "Not assessed". Upstream
//   exports its fallback values in that state.
// - provisionalLiveEvidence.bpm.valueBpm is always null: nothing in this
//   platform records a capture-time heart rate (upstream reads
//   pkg.liveEvidence / pkg.live / pkg.capture, which the pipeline never emits;
//   the same keys are read here for parity).
// - patientContextEvidence counts the bundle's questionnaire items
//   (sections[].items[]); a positive response is a boolean true (upstream's
//   rule) or the platform's "yes" answer.

import { APP_NAME, APP_VERSION, APP_BUILD_NOTE, MIN_ENERGY_RELIABLE_CYCLES } from "./constants"
import { fmtNumber } from "./format"
import { analysisQuality } from "./analysis-quality"
import { ENGINE_UNAVAILABLE } from "./evaluation-range"
import {
  ENGINE_RATIO_CALCULATION,
  evaluationRangeProvenance,
  timingStats,
  timingConsistency,
  ratePatternFromHr,
  rhythmPatternFromTiming,
  statusFromTiming,
  timingDisplayLabel,
  timingInternalLabel,
} from "./timing"
import { visualAcousticActivityStats, structuredTurbulenceEvidence, nrpeExperimentSchema } from "./evidence"
import { phaseRiskConfidence, phaseReviewNeeded } from "./phase"
import {
  statusFromSignalQuality,
  structuralSoundClassification,
  screeningStateFromStatus,
  buildScreeningSummary,
} from "./classifiers"

/** How the automated heart rate is named wherever it is compared with a reviewer-confirmed one. */
export const AUTOMATED_ESTIMATE_LABEL = "Automated estimate from the selected usable cycles"
/** The same, for a guide whose timing engine selected an Evaluation Range (upstream's wording). */
export const AUTOMATED_EVAL_RANGE_ESTIMATE_LABEL = "Automated Eval Range estimate"

/** A heart rate as a confirmed BPM is stored: a whole number 25–220, else null. */
export function normalizeBpm(value) {
  if (value === null || value === undefined || value === "") return null
  const bpm = Number(value)
  return Number.isFinite(bpm) && bpm >= 25 && bpm <= 220 ? Math.round(bpm) : null
}

/**
 * How a reviewer-entered (or capture-time) rate relates to the automated one:
 * the absolute difference and whether the two stand in a half/double
 * relation (ratio 1.8–2.2). Null / false when either rate is missing.
 */
export function compareBpm(enteredBpm, automatedBpm) {
  if (enteredBpm === null || !Number.isFinite(automatedBpm)) return { deltaBpm: null, halfDoubleRelation: false }
  const low = Math.min(enteredBpm, automatedBpm)
  const high = Math.max(enteredBpm, automatedBpm)
  const ratio = high / Math.max(low, 1)
  return { deltaBpm: Math.abs(enteredBpm - automatedBpm), halfDoubleRelation: ratio >= 1.8 && ratio <= 2.2 }
}

/**
 * The review note for a confirmed BPM: a disagreement of more than 10 bpm or a
 * half/double relation asks the report to keep both values; a difference of
 * more than 3 bpm is noted as slight; closer agreement needs no note.
 */
export function confirmedBpmReviewNoteFor({ deltaBpm, halfDoubleRelation }, estimateName = "automated estimate") {
  if (deltaBpm === null || deltaBpm <= 3) return null
  const rounded = Math.round(deltaBpm)
  return deltaBpm > 10 || halfDoubleRelation
    ? `HCP-entered BPM differs from the ${estimateName} by ${rounded} bpm; retain both values in evidence details.`
    : `HCP-entered BPM differs slightly from the ${estimateName} by ${rounded} bpm.`
}

// What the package reports in place of timing results the viewer withholds
// (timing engine present but not ready): timingStats' shape for a recording
// without usable cycles, and timingConsistency's "Limited" shape
const WITHHELD_TIMING_STATS = Object.freeze({
  cycles: [],
  hr: "-",
  n: 0,
  cycle: "-",
  sys: "-",
  dia: "-",
  ratio: "-",
  frac: "-",
  cv: "-",
  outliers: "-",
  s1IntervalCv: null,
})
const WITHHELD_TIMING_DETAIL =
  "the timing engine did not produce an Evaluation Range, so no timing results are reported"
const withheldTimingConsistency = () => ({
  label: "Limited",
  index: "-",
  detail: WITHHELD_TIMING_DETAIL,
  confidenceFlag: "Evaluation Range unavailable",
  timingConfidence: "N/A",
  metricUsed: "N/A",
  confidenceRationaleCodes: [],
  confidenceRationaleText: "",
  timingMethodFlags: [],
  usableCycleCount: 0,
})
// The Structural Sound classification the package reports when the viewer
// withholds its Energy values
const WITHHELD_STRUCTURAL_CLASSIFICATION = Object.freeze({
  status: "grey",
  label: "Structural sound not assessed",
  observed: "Not assessed",
  maxCyclePct: null,
  dominantPhase: null,
  energyStrength: "N/A",
  energyValue: null,
  phaseStatus: "unavailable",
  rule: "Structural-sound classification requires a timing-engine Evaluation Range; the timing engine did not produce one.",
})

const heartRateStatus = (bpm) => (bpm !== null && bpm >= 60 && bpm <= 100 ? "green" : "amber")

// A capture-time heart rate in a live-evidence object, under any of the key
// names upstream accepts; null when none
const findContextBpm = (value) => {
  if (!value || typeof value !== "object") return null
  const directKeys = [
    "bpm",
    "heartRateBpm",
    "heart_rate_bpm",
    "estimatedBpm",
    "estimated_bpm",
    "liveBpm",
    "live_bpm",
    "technicianBpm",
    "technician_bpm",
  ]
  for (const key of directKeys) {
    const bpm = normalizeBpm(value[key])
    if (bpm !== null) return bpm
  }
  for (const key of ["timingBasis", "timing", "heartRate", "heart_rate", "vitals", "capture"]) {
    const nested = value[key]
    const bpm = normalizeBpm(nested && (nested.bpm ?? nested.heartRateBpm ?? nested.heart_rate_bpm ?? nested.valueBpm))
    if (bpm !== null) return bpm
  }
  return null
}

// A questionnaire item answered in the affirmative: a boolean true under
// upstream's key names, or this platform's "yes" answer
const isPositiveResponse = (item) =>
  !!item &&
  (item.value === true ||
    item.checked === true ||
    item.selected === true ||
    item.answer === true ||
    String(item.a ?? "")
      .trim()
      .toLowerCase() === "yes")

export function buildCardiacReportEvidencePackage(ctx, options = {}) {
  const D = ctx.payload
  const examContext = options.examContext || {}
  const reviewStatus = options.reviewStatus || null
  const pkg = D.examPackage || {}
  const plausibilityGate = D.cardiacPlausibilityGate || pkg.cardiac_plausibility_gate || null
  const q = analysisQuality(ctx)
  // Timing engine present but not ready: the viewer shows every Provisional
  // Results value as "Unavailable", so nothing it withholds is exported
  const resultsWithheld = ctx.engine?.status === ENGINE_UNAVAILABLE
  const summaryAllowed = q.summaryAllowed && !resultsWithheld
  const stats = resultsWithheld ? WITHHELD_TIMING_STATS : timingStats(ctx)
  const timing = resultsWithheld ? withheldTimingConsistency() : timingConsistency(ctx)
  const ratePattern = summaryAllowed && stats.hr !== "-" ? ratePatternFromHr(stats.hr) : null
  const rhythmPattern = summaryAllowed ? rhythmPatternFromTiming(ctx, timing) : null
  const activity = resultsWithheld ? null : visualAcousticActivityStats(ctx, true)
  const evidence = structuredTurbulenceEvidence(ctx)
  const phaseRisk = phaseRiskConfidence(ctx)
  const nrpeExperiment = nrpeExperimentSchema(ctx, visualAcousticActivityStats(ctx, false))
  const recordingName = (pkg.audio && pkg.audio.name) || D.source || "uploaded.wav"
  const structuralClassification = resultsWithheld
    ? WITHHELD_STRUCTURAL_CLASSIFICATION
    : structuralSoundClassification(activity, evidence, q)
  // Energy observed without a phase is a result the viewer withholds too
  const unphasedEnergyDetected = !resultsWithheld && !!q.murmurBandEnergyEvidence?.substantial
  // The cycles behind the reported timing and Energy (none when withheld)
  const selectedTimingCycleIds = resultsWithheld ? [] : q.selectedTimingCycleIds || []
  const selectedTimingCycleCount = resultsWithheld ? 0 : q.selectedTimingCycleCount || 0
  const selectedCycleCount = Array.isArray(q.summaryCycles) && !resultsWithheld ? q.summaryCycles.length : null
  const energyCycleAlignment = resultsWithheld ? null : q.energyCycleAlignment || null
  const energyProvisional = !resultsWithheld && !!q.visualEnergyProvisional
  const phaseEnergyStatus = resultsWithheld ? "unavailable" : q.phaseEnergyStatus || "unavailable"
  const structuralLimitedReason = resultsWithheld
    ? WITHHELD_TIMING_DETAIL
    : q.energyReason || "not enough guide-quality cycles"
  let phaseEnergyReason = q.visualEnergyReason || q.energyReason || null
  if (resultsWithheld) phaseEnergyReason = WITHHELD_TIMING_DETAIL
  else if (phaseEnergyStatus === "scored") phaseEnergyReason = null
  const structuralSoundAssessable =
    !!activity &&
    !q.visualEnergyProvisional &&
    Number.isFinite(Number(activity.sysCyclePct)) &&
    Number.isFinite(Number(activity.diaCyclePct))
  const createdAt = options.now || new Date().toISOString()
  const recordingId = options.recordingId || `pcg_${Date.now()}`
  const examId = (pkg.exam && pkg.exam.examId) || pkg.examId || options.examId || `exam_${recordingId}`
  const recordingPosition = (pkg.audio && (pkg.audio.position || pkg.audio.site)) || null
  const automatedHeartRateBpm = summaryAllowed && stats.hr !== "-" ? Number(stats.hr) : null
  const recordingNotesForExport = Array.isArray(examContext.recordingNotes) ? examContext.recordingNotes : []
  const hcpConfirmedBpm = normalizeBpm(options.hcpConfirmedBpm)
  const confirmedComparison = compareBpm(hcpConfirmedBpm, automatedHeartRateBpm)
  // What the automated rate is compared as: the Evaluation Range's when the
  // timing engine selected one, else the selected usable cycles'.
  const evalRangeBasis = Boolean(ctx.evaluationRange)
  const automatedSource = evalRangeBasis ? "viewer_eval_range" : "viewer_curated_cycles"
  const automatedEstimateLabel = evalRangeBasis ? AUTOMATED_EVAL_RANGE_ESTIMATE_LABEL : AUTOMATED_ESTIMATE_LABEL
  // The timing engine's heart rate and SYS / DIA durations are medians over its
  // Evaluation Range; without the engine they are means over the usable cycles.
  const engineMedians = stats.ratioCalculation === ENGINE_RATIO_CALCULATION
  const automatedHeartRateFinding = engineMedians
    ? `Heart rate ${stats.hr} bpm from the median beat interval.`
    : `Average heart rate ${stats.hr} bpm across cycles.`
  const sysDiaDurationsFinding = `${engineMedians ? "median" : "mean"} systole ${stats.sys} ms and diastole ${
    stats.dia
  } ms across cycles`
  const confirmedBpmReviewNote = confirmedBpmReviewNoteFor(
    confirmedComparison,
    evalRangeBasis ? "automated Eval Range estimate" : "automated estimate"
  )
  const contextEvidencePolicy = {
    schema: "medaica.pcg.context_evidence_policy.v1",
    authority: "supporting_context",
    postProcessingPrimary: true,
    liveEvidenceSupportsReviewOnly: true,
    questionnaireSupportsContextOnly: true,
    doesNotOverrideAcousticMeasurements: true,
    absentContextIsNotABlocker: true,
    note: "Live exam data, technician observations, and questionnaire responses can support review context, but they do not replace post-processed Viewer measurements.",
  }
  const questionnaireForExport = examContext.questionnaire || null
  const patientContextForExport = examContext.patientContext || null
  const questionnaireResponses =
    questionnaireForExport && Array.isArray(questionnaireForExport.sections)
      ? questionnaireForExport.sections.flatMap((section) =>
          (Array.isArray(section.items) ? section.items : []).map((item) => ({
            section: section.title || null,
            ...item,
          }))
        )
      : []
  const liveEvidenceSources = [pkg.liveEvidence, pkg.live, pkg.capture].filter(Boolean)
  const provisionalLiveBpm = liveEvidenceSources.map(findContextBpm).find((value) => value !== null) ?? null
  const liveComparison = compareBpm(provisionalLiveBpm, automatedHeartRateBpm)
  const provisionalLiveEvidence = {
    schema: "medaica.pcg.provisional_live_evidence.v1",
    authority: "supporting_context",
    available:
      liveEvidenceSources.length > 0 ||
      recordingNotesForExport.some((note) => note && (note.technicianNote || note.note || note.text)),
    sourceCount: liveEvidenceSources.length,
    bpm: {
      valueBpm: provisionalLiveBpm,
      comparedTo: evalRangeBasis ? "viewer_eval_range_bpm" : "viewer_curated_cycle_bpm",
      [evalRangeBasis ? "viewerEvalRangeBpm" : "viewerCuratedCycleBpm"]: automatedHeartRateBpm,
      deltaBpm: liveComparison.deltaBpm === null ? null : Math.round(liveComparison.deltaBpm),
      halfDoubleRelation: liveComparison.halfDoubleRelation,
      status:
        liveComparison.deltaBpm === null
          ? "unavailable"
          : liveComparison.halfDoubleRelation
          ? "half_double_review"
          : liveComparison.deltaBpm <= 5
          ? "agree"
          : "different",
    },
    reviewerNotesPresent: recordingNotesForExport.length > 0,
    note: "Provisional live exam evidence is retained for review context and discrepancy checks only.",
  }
  const patientContextEvidence = {
    schema: "medaica.pcg.patient_context_evidence.v1",
    authority: "supporting_context",
    available: !!questionnaireForExport || !!patientContextForExport,
    questionnaireResponseCount: questionnaireResponses.length,
    positiveResponseCount: questionnaireResponses.filter(isPositiveResponse).length,
    patientContextPresent: !!patientContextForExport,
    note: "Questionnaire context may help prioritize review but does not create or override acoustic findings.",
  }
  const examScope = {
    scope: "single_recording",
    recordingCount: 1,
    coverageStatus: "single_recording_scope",
    coverageLabel: "Single recording",
    coverageNote:
      "Findings apply to this recording only. Single-recording scope does not reduce confidence in strong findings, but it does not rule out findings at other positions.",
    confidencePolicy:
      "Recording confidence is scored from the usable signal in this recording; exam coverage is reported separately from recording confidence.",
    standardPositionsRecorded: [],
  }
  const signalStatus = statusFromSignalQuality(q)
  const timingStatus = statusFromTiming(timing)
  const timingPatternLabel = rhythmPattern ? rhythmPattern.label : timing.label
  const timingPatternDisplayLabel = timingDisplayLabel(timingPatternLabel)
  const structuralStatus = structuralClassification.status
  const screeningSummary = buildScreeningSummary(signalStatus, timingStatus, structuralStatus, recordingName)
  const sdRatioValue = summaryAllowed && stats.ratio !== "-" ? Number(stats.ratio) : null
  const sdDisplay =
    Number.isFinite(sdRatioValue) && sdRatioValue > 0 ? `1 : ${(1 / sdRatioValue).toFixed(1)}` : "Not assessed"
  const provenance = evaluationRangeProvenance(ctx)
  const timingCalculationAudit = {
    schema: "medaica.pcg.timing_calculation_audit.v1",
    calculationVersion: "canonical-v0.7.1",
    sdRatioMethod: stats.ratioCalculation || null,
    meanSystolicMsExact: Number.isFinite(Number(stats.sysExactMs)) ? Number(stats.sysExactMs) : null,
    meanDiastolicMsExact: Number.isFinite(Number(stats.diaExactMs)) ? Number(stats.diaExactMs) : null,
    sdRatioExact: Number.isFinite(sdRatioValue) ? sdRatioValue : null,
    sdRatioDisplay: sdDisplay,
    selectedTimingCycleIds: selectedTimingCycleIds,
    selectedTimingCycleCount: selectedTimingCycleCount,
    examTimingReconciliation: options.timingReconciliation ?? null,
  }
  const phaseEnergyAudit = {
    schema: "medaica.pcg.phase_energy_audit.v1",
    policy: "energy_uses_exact_accepted_timing_cycle_ids",
    selectedTimingCycleIds: selectedTimingCycleIds,
    scoredEnergyCycleIds: activity && activity.cycleAlignment ? activity.cycleAlignment.scoredCycleIds : [],
    aligned:
      !!(activity && activity.cycleAlignment) &&
      JSON.stringify(activity.cycleAlignment.scoredCycleIds || []) === JSON.stringify(selectedTimingCycleIds),
    minimumCyclesForPhaseAssignment: q.phaseEnergyMinSelectedCycles || MIN_ENERGY_RELIABLE_CYCLES,
    selectedTimingCycleCount: selectedTimingCycleCount,
    status: phaseEnergyStatus,
    provisionalEstimateAvailable: energyProvisional,
    provisionalEstimateRequiresManualReview: energyProvisional,
    substantialUnphasedEnergy: unphasedEnergyDetected && !!q.visualEnergyLimited,
    phaseAssignmentLimited:
      !resultsWithheld && (phaseEnergyStatus === "limited_visible_energy" || !!q.visualEnergyPhaseLimited),
    reason: phaseEnergyReason,
  }
  return {
    schema: "MedaicaPcgViewerCardiacReportHandoff",
    version: "1.0",
    screeningSchema: "medaica.pcg.screening.v1",
    screeningVersion: "0.1",
    createdAt,
    examId,
    recordingId,
    exam: {
      examId,
      scope: examScope.scope,
      recordingCount: examScope.recordingCount,
      coverageStatus: examScope.coverageStatus,
      coverageLabel: examScope.coverageLabel,
      coverageNote: examScope.coverageNote,
      confidencePolicy: examScope.confidencePolicy,
      standardPositionsRecorded: examScope.standardPositionsRecorded,
    },
    examScope,
    reportPackage: "basic",
    sourceId: "pcg_viewer",
    sourceLabel: "Viewer",
    reportTarget: "cardiac_screening_report",
    productUse: {
      schema: "medaica.pcg.product_use_boundary.v1",
      analysisApproach: "deterministic_dsp_morphology",
      productUseMode: "viewer_hcp_review_aid",
      researchModelsExcluded: true,
      excludedFromProductUse: [
        "research_models",
        "murmur_triage_experiments",
        "research_only_marker_overlap",
        "research_only_nrpe",
      ],
      reportableEvidenceSource: "deterministic_timing_energy_and_hcp_review",
      bestHeardAtPolicy: "viewer_usable_position_and_hcp_selected_report_evidence",
      contextEvidenceDoesNotOverrideMeasurements: true,
      note: "Research-only model outputs are excluded from reportable product evidence unless a future validated release explicitly promotes them.",
    },
    reportEvidenceSource: {
      schema: "medaica.pcg.report_evidence_source.v1",
      policy: "best_heard_at_product_equivalent",
      selectionBasis:
        "Viewer usable-position evidence and HCP-selected report evidence replace external best-heard-at labels in product use.",
      selectedRecordingId: recordingId,
      selectedPosition: recordingPosition,
      timingSourceRecordingIds: [recordingId],
      structuralSoundSourceRecordingIds: [recordingId],
      reportableRecordingIds: [recordingId],
      hcpSelected: reviewStatus === "accepted",
    },
    calculationAudit: {
      timing: timingCalculationAudit,
      phaseEnergy: phaseEnergyAudit,
    },
    // Where a timing-engine guide's timing results came from; absent without the engine
    ...(provenance ? { evaluationRange: provenance } : {}),
    app: {
      name: APP_NAME,
      version: APP_VERSION,
      buildNote: APP_BUILD_NOTE,
    },
    recording: {
      recordingId,
      examId,
      evidenceScope: "recording_only",
      examScope: examScope.scope,
      coverageStatus: examScope.coverageStatus,
      coverageNote: examScope.coverageNote,
      name: recordingName,
      durationSec: D.duration || (pkg.audio && pkg.audio.duration) || null,
      sampleRateHz: D.sr_native || (pkg.audio && pkg.audio.sample_rate) || null,
      source: D.source || recordingName,
      audioKind: pkg.audio && pkg.audio.kind,
      cardiacPlausibilityGate: plausibilityGate,
    },
    recordings: [
      {
        recordingId,
        examId,
        evidenceScope: "recording_only",
        examScope: examScope.scope,
        coverageStatus: examScope.coverageStatus,
        coverageNote: examScope.coverageNote,
        name: recordingName,
        durationSec: D.duration || (pkg.audio && pkg.audio.duration) || null,
        sampleRateHz: D.sr_native || (pkg.audio && pkg.audio.sample_rate) || null,
        source: D.source || recordingName,
        audioKind: pkg.audio && pkg.audio.kind,
        position: recordingPosition,
        cardiacPlausibilityGate: plausibilityGate,
        acceptedForExamRollup: true,
      },
    ],
    evidence: {
      signalQuality: {
        observed: q.signalLabel || q.label || "Fair",
        status: signalStatus,
        sourceRecordingId: recordingId,
        sourcePosition: recordingPosition,
        finding: q.timingAllowed
          ? `Signal ${q.signalLabel || q.label || "Fair"}: ${fmtNumber(q.timingSeconds, 1)}s timing-guide audio; ${
              q.visualEnergyAllowed
                ? fmtNumber(q.visualEnergySeconds, 1) + "s structural-sound guide audio"
                : "structural-sound guide limited"
            }.`
          : `Signal unreliable: ${q.reason || "not enough usable guide-quality audio"}.`,
        confidence: q.markerConfidence,
        timingAllowed: q.timingAllowed,
        energyAllowed: q.energyAllowed,
        visualEnergyAllowed: q.visualEnergyAllowed,
        cardiacPlausibilityGate: plausibilityGate,
      },
      heartRate: {
        observed:
          hcpConfirmedBpm !== null
            ? `${hcpConfirmedBpm} bpm`
            : automatedHeartRateBpm !== null
            ? `${stats.hr} bpm`
            : "Not assessed",
        valueBpm: hcpConfirmedBpm !== null ? hcpConfirmedBpm : automatedHeartRateBpm,
        automatedObserved: automatedHeartRateBpm !== null ? `${stats.hr} bpm` : null,
        automatedValueBpm: automatedHeartRateBpm,
        hcpConfirmedBpm,
        acceptedSource: hcpConfirmedBpm !== null ? "hcp_entered_viewer" : automatedSource,
        reviewNote: confirmedBpmReviewNote,
        bpmDelta: confirmedComparison.deltaBpm,
        halfDoubleRelation: confirmedComparison.halfDoubleRelation,
        supportingLiveBpmReview: provisionalLiveEvidence.bpm,
        status: heartRateStatus(hcpConfirmedBpm !== null ? hcpConfirmedBpm : automatedHeartRateBpm),
        sourceRecordingId: recordingId,
        sourcePosition: recordingPosition,
        finding:
          hcpConfirmedBpm !== null
            ? [
                `Accepted BPM entered in Viewer: ${hcpConfirmedBpm} bpm.`,
                automatedHeartRateBpm !== null ? `${automatedEstimateLabel}: ${stats.hr} bpm.` : "",
                confirmedBpmReviewNote || "",
              ]
                .filter(Boolean)
                .join(" ")
            : automatedHeartRateBpm !== null
            ? automatedHeartRateFinding
            : "Heart-rate guide unavailable from this recording.",
      },
      timingSummary: {
        observed: timing.label && timing.label !== "Limited" ? timingDisplayLabel(timing.label) : "Limited timing",
        status: timingStatus,
        sourceRecordingId: recordingId,
        sourcePosition: recordingPosition,
        finding: summaryAllowed
          ? `Timing guide available: ${stats.hr} bpm, ${timingDisplayLabel(
              timing.label
            ).toLowerCase()} cycles, cardiac cycle timing ${sdDisplay}. PCG timing estimate only.`
          : `Timing guide limited: ${timing.detail || q.reason || "not enough usable cycles"}.`,
        regularityPct: typeof timing.closePct === "number" ? timing.closePct : null,
        timingIndex: typeof timing.index === "number" ? timing.index : null,
        rateRange: ratePattern ? ratePattern.label : null,
        rhythmPattern: rhythmPattern ? rhythmPattern.label : null,
        displayRhythmPattern: rhythmPattern ? timingDisplayLabel(rhythmPattern.label) : null,
        cycles: stats.n || 0,
      },
      systolicDiastolicTiming: {
        observed: sdDisplay,
        normalRange: "0.40 – 0.67",
        displayReferenceRange: "Expected resting range: 1 : 1.5 to 1 : 2.5",
        status: Number.isFinite(sdRatioValue) && sdRatioValue >= 0.4 && sdRatioValue <= 0.67 ? "green" : "amber",
        sourceRecordingId: recordingId,
        sourcePosition: recordingPosition,
        finding:
          summaryAllowed && stats.ratio !== "-"
            ? `Cardiac cycle timing ${sdDisplay}; ${sysDiaDurationsFinding}.`
            : "Cardiac cycle timing guide unavailable from this recording.",
        rawSdRatio: sdRatioValue,
        calculationMethod: stats.ratioCalculation || null,
        selectedTimingCycleIds: selectedTimingCycleIds,
        selectedTimingCycleCount: selectedTimingCycleCount,
        meanSystolicMsExact: Number.isFinite(Number(stats.sysExactMs)) ? Number(stats.sysExactMs) : null,
        meanDiastolicMsExact: Number.isFinite(Number(stats.diaExactMs)) ? Number(stats.diaExactMs) : null,
        sysMs: summaryAllowed && stats.sys !== "-" ? Number(stats.sys) : null,
        diaMs: summaryAllowed && stats.dia !== "-" ? Number(stats.dia) : null,
      },
      cardiacCycleTiming: {
        observed: sdDisplay,
        normalRange: "0.40 – 0.67",
        displayReferenceRange: "Expected resting range: 1 : 1.5 to 1 : 2.5",
        status: Number.isFinite(sdRatioValue) && sdRatioValue >= 0.4 && sdRatioValue <= 0.67 ? "green" : "amber",
        sourceRecordingId: recordingId,
        sourcePosition: recordingPosition,
        finding:
          summaryAllowed && stats.ratio !== "-"
            ? `Cardiac cycle timing ${sdDisplay}; ${sysDiaDurationsFinding}.`
            : "Cardiac cycle timing guide unavailable from this recording.",
        rawSdRatio: sdRatioValue,
        calculationMethod: stats.ratioCalculation || null,
        selectedTimingCycleIds: selectedTimingCycleIds,
        selectedTimingCycleCount: selectedTimingCycleCount,
        meanSystolicMsExact: Number.isFinite(Number(stats.sysExactMs)) ? Number(stats.sysExactMs) : null,
        meanDiastolicMsExact: Number.isFinite(Number(stats.diaExactMs)) ? Number(stats.diaExactMs) : null,
        sysMs: summaryAllowed && stats.sys !== "-" ? Number(stats.sys) : null,
        diaMs: summaryAllowed && stats.dia !== "-" ? Number(stats.dia) : null,
      },
      rhythmRegularity: {
        observed:
          typeof timing.timingSwingPct === "number"
            ? `±${fmtNumber(timing.timingSwingPct, 1)}% timing variation · ${timingPatternDisplayLabel}`
            : "Not assessed",
        normalRange: "Experimental PCG timing variability bands",
        displayReferenceRange:
          "Experimental PCG timing variability bands: Normal up to about ±8% (provider-only adjustment to ±11% when HR <60) · Moderate ±8-18% · Elevated >±18% (reference point ±25%)",
        status: statusFromTiming(timing),
        sourceRecordingId: recordingId,
        sourcePosition: recordingPosition,
        finding:
          typeof timing.closePct === "number"
            ? `Approximate S1-to-S1 timing variation ±${fmtNumber(timing.timingSwingPct, 1)}% (about ±${fmtNumber(
                timing.timingSwingMs,
                0
              )} ms around a median cycle length of ${fmtNumber(
                timing.medianCycleMs,
                0
              )} ms) across selected usable cycles. Metric: ${
                timing.metricUsed || timing.spreadMethod || "timing spread"
              }. PCG timing estimate; confirm with clinical review when needed.`
            : "Rhythm regularity guide unavailable from this recording.",
        timingLabel: timingPatternDisplayLabel,
        timingConfidence: timing.timingConfidence || null,
        confidenceRationaleCodes: Array.isArray(timing.confidenceRationaleCodes) ? timing.confidenceRationaleCodes : [],
        confidenceRationaleText: timing.confidenceRationaleText || "",
        timingMethodFlags: Array.isArray(timing.timingMethodFlags) ? timing.timingMethodFlags : [],
        metricUsed: timing.metricUsed || null,
        metric_used: timing.metricUsed || null,
        regularityPct: typeof timing.closePct === "number" ? timing.closePct : null,
        timingIndex: typeof timing.index === "number" ? timing.index : null,
        cycleVariability: typeof timing.cv === "number" ? timing.cv : null,
        successiveDifferencePct:
          typeof timing.successiveDifferencePct === "number" ? timing.successiveDifferencePct : null,
        lag1Autocorrelation: typeof timing.lag1Autocorrelation === "number" ? timing.lag1Autocorrelation : null,
        timingVariabilityPct: typeof timing.robustSpreadPct === "number" ? timing.robustSpreadPct : null,
        robustSpreadPct: typeof timing.robustSpreadPct === "number" ? timing.robustSpreadPct : null,
        timingSwingPct: typeof timing.timingSwingPct === "number" ? timing.timingSwingPct : null,
        robustSpreadMs: typeof timing.robustSpreadMs === "number" ? timing.robustSpreadMs : null,
        timingSwingMs: typeof timing.timingSwingMs === "number" ? timing.timingSwingMs : null,
        medianCycleMs: typeof timing.medianCycleMs === "number" ? timing.medianCycleMs : null,
        expectedNormalVariabilityPct:
          typeof timing.expectedNormalVariabilityPct === "number" ? timing.expectedNormalVariabilityPct : null,
        expectedNormalSwingPct:
          typeof timing.expectedNormalSwingPct === "number" ? timing.expectedNormalSwingPct : null,
        irregularVariabilityPct:
          typeof timing.irregularVariabilityPct === "number" ? timing.irregularVariabilityPct : null,
        irregularSwingPct: typeof timing.irregularSwingPct === "number" ? timing.irregularSwingPct : null,
        highConfidenceMarkerPct:
          typeof timing.highConfidenceMarkerPct === "number" ? timing.highConfidenceMarkerPct : null,
        inferredMarkerPct: typeof timing.inferredMarkerPct === "number" ? timing.inferredMarkerPct : null,
        weakBoundaryPct: typeof timing.weakBoundaryPct === "number" ? timing.weakBoundaryPct : null,
        s1s2GuideQuality: timing.guideQuality || null,
        s1s2OrientationScore: typeof timing.orientationScore === "number" ? timing.orientationScore : null,
        noiseFloorFlag: !!timing.noiseFloorFlag,
        rateThresholdAdjusted:
          Array.isArray(timing.timingMethodFlags) && timing.timingMethodFlags.includes("rate_adjustment_applied"),
        rateThresholdAdjustedBpm:
          Array.isArray(timing.timingMethodFlags) && timing.timingMethodFlags.includes("rate_adjustment_applied")
            ? timing.bpmForThreshold
            : null,
        elevated25pctReference: typeof timing.timingSwingPct === "number" ? timing.timingSwingPct > 25 : false,
        spreadMethod: timing.spreadMethod || null,
        timingConfidenceFlag: timing.confidenceFlag || null,
        selectedCycleCount: selectedCycleCount,
        rawMarkerConfidence: typeof q.rawMarkerConfidence === "number" ? q.rawMarkerConfidence : null,
        timingCycleConfidence: typeof q.timingCycleConfidence === "number" ? q.timingCycleConfidence : null,
        markerConfidence: typeof q.markerConfidence === "number" ? q.markerConfidence : null,
        rateRange: ratePattern ? ratePattern.label : null,
        rhythmPattern: rhythmPattern ? timingInternalLabel(rhythmPattern.label) : null,
        displayRhythmPattern: timingPatternDisplayLabel,
        outlierCount: typeof timing.outlierCount === "number" ? timing.outlierCount : null,
        basis:
          "PCG-derived beat-to-beat timing variation proxy: selected usable S1-to-S1 central spread with experimental timing variability bands",
      },
      s1s2IntensityBalance: {
        observed:
          D.metrics && typeof D.metrics.s1s2_amp_ratio === "number"
            ? fmtNumber(D.metrics.s1s2_amp_ratio, 2)
            : "Not assessed",
        status:
          D.metrics &&
          typeof D.metrics.s1s2_amp_ratio === "number" &&
          D.metrics.s1s2_amp_ratio >= 1.0 &&
          D.metrics.s1s2_amp_ratio <= 2.0
            ? "green"
            : "amber",
        sourceRecordingId: recordingId,
        sourcePosition: recordingPosition,
        finding:
          D.metrics && typeof D.metrics.s1s2_amp_ratio === "number"
            ? `S1/S2 intensity ratio ${fmtNumber(D.metrics.s1s2_amp_ratio, 2)} across cycles. Guide measurement only.`
            : "S1/S2 intensity balance unavailable from this recording.",
      },
      structuralSoundSummary: {
        observed: structuralClassification.observed,
        status: structuralStatus,
        sourceRecordingId: recordingId,
        sourcePosition: recordingPosition,
        finding: activity
          ? `Structural-sound guide: Sys ${fmtNumber(activity.sysCyclePct, 0)}%, Dia ${fmtNumber(
              activity.diaCyclePct,
              0
            )}%, scored cycles ${activity.cycles}. Dominant guide: ${structuralClassification.dominantPhase} ${
              structuralClassification.energyStrength
            } / Cycles ${fmtNumber(
              structuralClassification.maxCyclePct,
              0
            )}%. Status uses cycle repeatability only: <50% Normal, 50-75% Borderline, >75% Elevated.`
          : structuralClassification.phaseStatus === "pending_review"
          ? "Extra heart-sound energy was seen, but its timing within the heartbeat could not be measured reliably. SYS/DIA attribution is held for HCP review."
          : `Structural-sound assessment limited: ${structuralLimitedReason}.`,
        sysCyclePct: activity ? activity.sysCyclePct : null,
        diaCyclePct: activity ? activity.diaCyclePct : null,
        maxCyclePct: structuralClassification.maxCyclePct,
        dominantPhase: structuralClassification.dominantPhase,
        energyStrength: structuralClassification.energyStrength,
        energyValue: structuralClassification.energyValue,
        phaseStatus: structuralClassification.phaseStatus || (structuralSoundAssessable ? "assigned" : "unavailable"),
        unphasedEnergyDetected,
        patientText:
          structuralClassification.phaseStatus === "pending_review"
            ? "Extra heart-sound energy was seen. Its timing within the heartbeat could not be measured reliably. Review with clinical context."
            : null,
        providerText:
          structuralClassification.phaseStatus === "pending_review"
            ? "Repeatable murmur-band Energy was observed, but SYS/DIA attribution is withheld pending phase review. Interpret with the recording, examination, history, and symptoms."
            : null,
        structuralRule: structuralClassification.rule,
        cycleAlignment: energyCycleAlignment,
        selectedTimingCycleIds: selectedTimingCycleIds,
        scoredEnergyCycleIds: activity && activity.cycleAlignment ? activity.cycleAlignment.scoredCycleIds : [],
        scoredCycles: activity ? activity.cycles : 0,
        score: evidence && !resultsWithheld ? evidence.score : null,
        scoreFlag: evidence && !resultsWithheld ? evidence.scoreFlag : null,
      },
      structuralSoundDetail: {
        observed: activity
          ? `Sys ${fmtNumber(activity.sysCyclePct, 0)}% / Dia ${fmtNumber(activity.diaCyclePct, 0)}%`
          : structuralClassification.phaseStatus === "pending_review"
          ? "Phase pending review"
          : "Not assessed",
        status: structuralStatus,
        sourceRecordingId: recordingId,
        sourcePosition: recordingPosition,
        finding: activity
          ? `Repeated structural-sound guide activity: Sys ${fmtNumber(activity.sysCyclePct, 0)}%, Dia ${fmtNumber(
              activity.diaCyclePct,
              0
            )}%, scored cycles ${activity.cycles}.`
          : structuralClassification.phaseStatus === "pending_review"
          ? "Phase-independent Energy observed; SYS/DIA detail withheld pending HCP phase review."
          : "Repeated structural-sound detail unavailable from this recording.",
      },
    },
    screening: {
      examScope,
      summary: screeningSummary,
      timingSummary: {
        state: screeningStateFromStatus(timingStatus),
        label: timing.label && timing.label !== "Limited" ? timingDisplayLabel(timing.label) : "Limited timing",
        internalLabel: timing.label && timing.label !== "Limited" ? timing.label : "Limited timing",
        timingConfidence: timing.timingConfidence || null,
        confidenceRationaleCodes: Array.isArray(timing.confidenceRationaleCodes) ? timing.confidenceRationaleCodes : [],
        confidenceRationaleText: timing.confidenceRationaleText || "",
        timingMethodFlags: Array.isArray(timing.timingMethodFlags) ? timing.timingMethodFlags : [],
        metricUsed: timing.metricUsed || null,
        metric_used: timing.metricUsed || null,
        bpm: summaryAllowed && stats.hr !== "-" ? Number(stats.hr) : null,
        regularPct: typeof timing.closePct === "number" ? timing.closePct : null,
        cycleVariability: !resultsWithheld && typeof D.metrics?.rr_cv === "number" ? D.metrics.rr_cv : null,
        successiveDifferencePct:
          typeof timing.successiveDifferencePct === "number" ? timing.successiveDifferencePct : null,
        lag1Autocorrelation: typeof timing.lag1Autocorrelation === "number" ? timing.lag1Autocorrelation : null,
        timingVariabilityPct: typeof timing.robustSpreadPct === "number" ? timing.robustSpreadPct : null,
        robustSpreadPct: typeof timing.robustSpreadPct === "number" ? timing.robustSpreadPct : null,
        timingSwingPct: typeof timing.timingSwingPct === "number" ? timing.timingSwingPct : null,
        robustSpreadMs: typeof timing.robustSpreadMs === "number" ? timing.robustSpreadMs : null,
        timingSwingMs: typeof timing.timingSwingMs === "number" ? timing.timingSwingMs : null,
        medianCycleMs: typeof timing.medianCycleMs === "number" ? timing.medianCycleMs : null,
        expectedNormalVariabilityPct:
          typeof timing.expectedNormalVariabilityPct === "number" ? timing.expectedNormalVariabilityPct : null,
        expectedNormalSwingPct:
          typeof timing.expectedNormalSwingPct === "number" ? timing.expectedNormalSwingPct : null,
        irregularVariabilityPct:
          typeof timing.irregularVariabilityPct === "number" ? timing.irregularVariabilityPct : null,
        irregularSwingPct: typeof timing.irregularSwingPct === "number" ? timing.irregularSwingPct : null,
        highConfidenceMarkerPct:
          typeof timing.highConfidenceMarkerPct === "number" ? timing.highConfidenceMarkerPct : null,
        inferredMarkerPct: typeof timing.inferredMarkerPct === "number" ? timing.inferredMarkerPct : null,
        weakBoundaryPct: typeof timing.weakBoundaryPct === "number" ? timing.weakBoundaryPct : null,
        s1s2GuideQuality: timing.guideQuality || null,
        s1s2OrientationScore: typeof timing.orientationScore === "number" ? timing.orientationScore : null,
        noiseFloorFlag: !!timing.noiseFloorFlag,
        rateThresholdAdjusted:
          Array.isArray(timing.timingMethodFlags) && timing.timingMethodFlags.includes("rate_adjustment_applied"),
        rateThresholdAdjustedBpm:
          Array.isArray(timing.timingMethodFlags) && timing.timingMethodFlags.includes("rate_adjustment_applied")
            ? timing.bpmForThreshold
            : null,
        elevated25pctReference: typeof timing.timingSwingPct === "number" ? timing.timingSwingPct > 25 : false,
        rateRange: ratePattern ? ratePattern.label : null,
        rhythmPattern: rhythmPattern ? timingInternalLabel(rhythmPattern.label) : null,
        spreadMethod: timing.spreadMethod || null,
        timingConfidenceFlag: timing.confidenceFlag || null,
        selectedCycleCount: selectedCycleCount,
        rawMarkerConfidence: typeof q.rawMarkerConfidence === "number" ? q.rawMarkerConfidence : null,
        timingCycleConfidence: typeof q.timingCycleConfidence === "number" ? q.timingCycleConfidence : null,
        markerConfidence: typeof q.markerConfidence === "number" ? q.markerConfidence : null,
        meanSysMs: summaryAllowed && stats.sys !== "-" ? Number(stats.sys) : null,
        meanDiaMs: summaryAllowed && stats.dia !== "-" ? Number(stats.dia) : null,
        meanSdRatio: summaryAllowed && stats.ratio !== "-" ? Number(stats.ratio) : null,
        basis: "S1-to-S1 timing variation plus S1-S2/S2-S1 timing windows",
      },
      structuralSoundSummary: {
        state: screeningStateFromStatus(structuralStatus),
        label: structuralClassification.label,
        observed: structuralClassification.observed,
        sysRepeatedPct: activity ? activity.sysCyclePct : null,
        diaRepeatedPct: activity ? activity.diaCyclePct : null,
        maxRepeatedPct: structuralClassification.maxCyclePct,
        dominantPhase: structuralClassification.dominantPhase,
        energyStrength: structuralClassification.energyStrength,
        phaseStatus: structuralClassification.phaseStatus || (structuralSoundAssessable ? "assigned" : "unavailable"),
        unphasedEnergyDetected,
        patientText:
          structuralClassification.phaseStatus === "pending_review"
            ? "Extra heart-sound energy was seen. Its timing within the heartbeat could not be measured reliably. Review with clinical context."
            : null,
        providerText:
          structuralClassification.phaseStatus === "pending_review"
            ? "Repeatable murmur-band Energy was observed, but SYS/DIA attribution is withheld pending phase review. Interpret with the recording, examination, history, and symptoms."
            : null,
        cycleAlignment: energyCycleAlignment,
        scoredCycles: activity ? activity.cycles : 0,
        basis: structuralClassification.rule,
      },
      followUpNote: {
        state:
          screeningSummary.state === "Elevated"
            ? "Review"
            : screeningSummary.state === "Borderline"
            ? "Review or repeat"
            : "Routine",
        placement: "report_footer",
        consumerText:
          screeningSummary.state === "Elevated"
            ? "This screening found an extra heart-sound pattern. Please review it with a healthcare professional. Seek urgent care for chest pain, fainting, severe shortness of breath, or other urgent symptoms."
            : "This screening does not replace medical evaluation. Repeat if symptoms change or a clinician asks for another recording.",
        hcpText:
          "This screening report is intended to support clinician assessment, not replace it. Interpret alongside relevant history, symptoms, physical examination, and standard clinical testing when clinically indicated.",
      },
    },
    reliability: {
      signal: q.signalLabel || q.label || "Fair",
      status: signalStatus,
      examScope: examScope.scope,
      coverageStatus: examScope.coverageStatus,
      coverageNote: examScope.coverageNote,
      usableTimingSec: q.timingSeconds || 0,
      usableStructuralSec: q.visualEnergySeconds || 0,
      timingAllowed: !!q.timingAllowed,
      structuralSoundAllowed: structuralSoundAssessable,
      selectedMarkerConfidence: q.markerConfidence || null,
    },
    viewerEvidence: {
      rawWaveformAvailable: true,
      s1s2GuidesAvailable: !!q.timingAllowed,
      sysDiaGuidesAvailable: !!q.timingAllowed,
      cycleEnergyGuideAvailable: structuralSoundAssessable,
      selectedRanges: [],
    },
    evidenceFeeds: {
      pcgViewer: {
        status: reviewStatus === "accepted" ? "attested" : reviewStatus || "unreviewed",
        sourceId: "pcg_viewer",
        createdAt,
        recordingIds: [recordingId],
        clinicianAttestation: {
          state: reviewStatus === "accepted" ? "attested" : "unreviewed",
          meaning: "Reviewed and considered adequate for this exam; does not override the calculated value.",
        },
        appliedInterpretation: ctx.phaseReviewResolution === "alternate" ? "alternate" : "primary",
        alternatesExist: phaseReviewNeeded(ctx, q),
        alternateInterpretations: phaseReviewNeeded(ctx, q)
          ? [
              {
                id: "alternate_phase_alignment",
                location: "research_audit",
                applied: ctx.phaseReviewResolution === "alternate",
              },
            ]
          : [],
      },
      act: null,
      beatAware: null,
    },
    contextInputs: {
      contextEvidencePolicy,
      provisionalLiveEvidence,
      patientContextEvidence,
      currentQuestionnaire: questionnaireForExport,
      patientContext: patientContextForExport,
      recordingNotes: recordingNotesForExport,
      flags: {},
      note: "Patient-reported questionnaire context is read-only and not independently verified. Viewer observations are internal review context unless a separate Report note is supplied.",
    },
    context: {
      schema: "ExamContextPackage",
      version: "1.0",
      contextEvidencePolicy,
      provisionalLiveEvidence,
      patientContextEvidence,
      currentQuestionnaire: questionnaireForExport,
      patientContext: patientContextForExport,
      recordingNotes: recordingNotesForExport,
      contextFlags: {},
      priorExams: [],
      priorQuestionnaires: [],
      longitudinal: {
        hasPrior: false,
        summary: "No prior Medaica exam available for comparison.",
        trendWindow: null,
        changes: [],
      },
      provenance: {
        source: "pcg_viewer_context_hook",
        note: "Day-one hook for questionnaire and longitudinal context. Acoustic confidence remains separate from context-adjusted review priority.",
      },
    },
    research: {
      excludedFromProductUse: true,
      productUseStatus: "excluded_research_only",
      note: "Research-only model outputs are not used as accepted report evidence in this product version.",
      murmurTriage: pkg.murmur || null,
      structuralTurbulenceEvidence: evidence || null,
      phaseRisk,
      normalizedReferencePhaseEnergyExperiment: nrpeExperiment,
    },
    guideMetadata: {
      viewerVersion: pkg.version || "0.5",
      analysisApproach: "deterministic_dsp_morphology",
      researchModelsExcluded: true,
      timingModel: "s1s2_layout_forest_v001",
      structuralSoundModel: "turbulent_energy_forest_v003",
      provenance: (pkg.provenance && pkg.provenance.processing) || "local viewer pipeline",
      createdAt,
    },
    hcpReview: {
      status: "pending",
      reviewedBy: null,
      reviewedAt: null,
      notes: "",
      recordingNotes: recordingNotesForExport,
      recordingNoteInstruction:
        "Viewer observations are internal HCP review context. Only the separate Report note is intended for the patient and care-team report.",
    },
    confidence: {
      phase: phaseRisk,
      modelConfidence: pkg.confidence || {},
      quality: q,
    },
    provenance: {
      viewerVersion: pkg.version || APP_VERSION,
      modelVersion: pkg.model_version || "pcg-viewer-v0.6.3-local",
      analysisApproach: "deterministic_dsp_morphology",
      researchModelsExcluded: true,
      processing: (pkg.provenance && pkg.provenance.processing) || "local viewer pipeline",
      note: "Result prepared in Medaica Viewer for downstream report preparation.",
    },
  }
}
